import { isProduction } from 'utils/env/isProduction';

const STORAGE_KEY = 'returnUrl';
// a stale value from an abandoned login flow must not hijack an unrelated later login in the same tab:
const MAX_AGE_IN_MS = 10 * 60 * 1000;
// routes that must never be a post-login destination: auth pages (redirect loop) and token-handoff
// endpoints that would swap the just-issued session (setAccessToken, oauth authorize-callbacks).
// lower-case - react-router matches paths case-insensitively, so the comparison must too:
const SENSITIVE_PATH_PREFIXES = ['/auth', '/setaccesstoken', '/redirect/auth'];

export default class ReturnUrlService {
	constructor(sessionStorage, navigationService) {
		this._sessionStorage = sessionStorage;
		this._navigationService = navigationService;
	}

	/**
	 * builds the login url for the given location, attaching the current address as returnUrl only when it is
	 * safe to return to - single source of truth for both producers (this) and the consumer (capture/pickUp).
	 * @param {{ pathname: string, search?: string, hash?: string }} location
	 * @return {string}
	 */
	loginUrl({ pathname, search, hash }) {
		const target = this._normalize(pathname + (search || '') + (hash || ''));

		return target ? `/auth/login?returnUrl=${encodeURIComponent(target)}` : '/auth/login';
	}

	redirectAfterLogin() {
		// loadReplace, not load - the login page entry must not stay in history and bounce a Back press forward:
		this._navigationService.loadReplace(this.pickUp() || '/');
	}

	capture(url) {
		const target = this._normalize(url);

		if (target) {
			this._sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ url: target, createdAt: Date.now() }));
		}
	}

	pickUp() {
		const stored = this._sessionStorage.getItem(STORAGE_KEY);

		this._sessionStorage.removeItem(STORAGE_KEY);

		if (!stored) {
			return null;
		}

		try {
			const { url, createdAt } = JSON.parse(stored);

			// reject a missing/non-numeric timestamp - Date.now() - undefined is NaN, and NaN > MAX is false,
			// which would silently disable the staleness guard instead of dropping the entry:
			if (!Number.isFinite(createdAt) || Date.now() - createdAt > MAX_AGE_IN_MS) {
				return null;
			}

			return this._normalize(url);
		} catch (error) {
			return null;
		}
	}

	clear() {
		this._sessionStorage.removeItem(STORAGE_KEY);
	}

	/**
	 * resolves a candidate returnUrl to a safe navigation target, or null when it must not be a post-login
	 * destination. returns the RESOLVED value (a root-absolute path, or a full url for the dev panel host) so
	 * that what we validate is exactly what the browser later navigates to - a raw relative string like "logout"
	 * would otherwise validate against "/" here but resolve against "/auth/" at the sink and reach /auth/logout.
	 * @param {string} url
	 * @return {string|null}
	 */
	_normalize(url) {
		if (!url || typeof url !== 'string') {
			return null;
		}

		// reject chars the URL parser strips (tab/newline/CR) - "/\t/evil.com" would resolve to "//evil.com":
		if (/[\t\n\r]/.test(url)) {
			return null;
		}

		let resolved;

		try {
			resolved = new URL(url, window.location.origin);
		} catch (error) {
			return null;
		}

		if (this._isSensitivePathname(resolved.pathname)) {
			return null;
		}

		// a resolved pathname of "//host" (from dot-segment tricks like "/.//evil.com") is protocol-relative at
		// the sink and would navigate cross-origin - reject so capture never persists an open-redirect payload:
		if (resolved.pathname.startsWith('//')) {
			return null;
		}

		const path = resolved.pathname + resolved.search + resolved.hash;

		// the site root is not a meaningful destination:
		if (resolved.pathname === '/' && !resolved.search && !resolved.hash) {
			return null;
		}

		if (resolved.origin === window.location.origin) {
			return path;
		}

		// dev only - admin (/panel) runs on a separate host:
		if (!isProduction && process.env.REACT_APP_PANEL_HOST) {
			try {
				if (resolved.origin === new URL(process.env.REACT_APP_PANEL_HOST).origin) {
					return resolved.href;
				}
			} catch (error) {
				return null;
			}
		}

		return null;
	}

	_isSensitivePathname(pathname) {
		const lower = pathname.toLowerCase();

		return SENSITIVE_PATH_PREFIXES.some(prefix => lower === prefix || lower.startsWith(`${prefix}/`));
	}
}
